import { useEffect } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { useBookCall } from "../components/BookCallDialog";
import { useSeo, faqPageSchema, serviceSchema } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  ArrowUpRight, ShieldCheck, Globe2, MapPin, Languages, Zap, Sparkles, HeartHandshake,
  Plane, Briefcase, Home as HomeIcon, Users, Building2, KeyRound, Rocket
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg";
const PREMIUM_IMG = "https://images.pexels.com/photos/14505912/pexels-photo-14505912.jpeg";
const BASIC_IMG = "https://images.unsplash.com/photo-1772338537689-056082f100a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxtYW4lMjBhc3NlbWJsaW5nJTIwZnVybml0dXJlJTIwaG9tZXxlbnwwfHx8fDE3Nzc4MjA2NDZ8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1758523671087-b256bbbca475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNvdXBsZSUyMHVucGFja2luZyUyMGFwYXJ0bWVudHxlbnwwfHx8fDE3Nzc4MjA2MzR8MA&ixlib=rb-4.1.0&q=85";
const MUNICH_IMG = "https://images.unsplash.com/photo-1652694664151-0dbc97712aaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtdW5pY2glMjBzdHJlZXQlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc3ODIwNjM0fDA&ixlib=rb-4.1.0&q=85";
const CONCIERGE_IMG = "https://images.unsplash.com/photo-1758518727888-ffa196002e59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb25jaWVyZ2UlMjB3ZWxjb21pbmd8ZW58MHx8fHwxNzc3ODIwNjQ2fDA&ixlib=rb-4.1.0&q=85";

const useCases = [
  "You are moving to Munich",
  "You are moving within Munich",
  "You are setting up your apartment after signing a lease",
  "You are transitioning from temporary to permanent housing",
  "You need your home office ready before your first workday",
  "You need your home ready before your family arrives",
];

const problems = [
  "Language barriers with retailers and delivery companies",
  "Empty apartments with no furniture and even no kitchen",
  "Coordinating deliveries while handling Anmeldung, banking, and bureaucracy",
  "Assembling furniture after long international moves",
  "Managing packaging disposal and recycling rules",
  "Setting up a functional home office quickly",
  "Adjusting your home after moving in (new furniture, replacements, upgrades)",
];

const moveInItems = [
  "Furniture planning & product list",
  "Ordering coordination",
  "Delivery scheduling",
  "Furniture assembly",
  "Room setup (bedroom, living room, workspace)",
  "Packaging removal and disposal",
];
const beyondItems = [
  "Home office setup before your first workday",
  "Additional furniture assembly and upgrades",
  "Returns and replacement coordination",
  "Apartment reconfiguration (new needs, family changes)",
  "Move-out preparation and support",
];

const steps = [
  { n: "01", title: "Discovery Call", desc: "We understand your apartment, timeline, priorities, and current situation — move-in, upgrade, or transition." },
  { n: "02", title: "Planning & Coordination", desc: "You share preferences or a product list. We source from trusted providers — IKEA, Home24, XXXLutz." },
  { n: "03", title: "Delivery & Setup", desc: "We coordinate deliveries, assemble furniture, and set up your space." },
  { n: "04", title: "Ongoing Support", desc: "Optional — we remain available for adjustments, additions, and future needs." },
  { n: "05", title: "Move Into a Ready Home", desc: "Arrive — or continue your life — without disruption." },
];

const audiences = [
  { icon: Plane, label: "Expats relocating to Munich" },
  { icon: Briefcase, label: "Professionals starting a new role" },
  { icon: HomeIcon, label: "Remote workers needing a functional setup" },
  { icon: Users, label: "Families arriving or expanding" },
  { icon: KeyRound, label: "Tenants moving from temporary to long-term housing" },
  { icon: Building2, label: "Corporate HR / relocation partners" },
  { icon: Sparkles, label: "Furnished apartment hosts and landlords" },
];

const moreSituations = [
  "Moving from temporary housing (Airbnb, serviced apartment) to a permanent home",
  "Setting up an unfurnished apartment from scratch",
  "Preparing your home office before your first working day",
  "Adjusting your home after settling in (new furniture, replacements, upgrades)",
  "Supporting family arrivals or changing living needs",
  "Preparing apartments for move-out or subletting",
  "Standardizing and managing corporate housing setups",
];

const packages = [
  {
    code: "S",
    title: "IKEA Assembly",
    audience: "For IKEA shoppers — PAX, MALM, BEKANT, BILLY and more",
    desc: "Dedicated IKEA assembly service in Munich — from a single PAX wardrobe to a complete IKEA-furnished room.",
    includes: ["PAX, PLATSA, BRIMNES wardrobes", "MALM, HEMNES, SONGESAND beds", "BEKANT, MICKE, ALEX desks", "Delivery receiving", "Packaging removal"],
    price: "Starting from €450",
    img: BASIC_IMG,
    tag: "IKEA only",
    link: "/ikea-assembly-munich",
    learnMore: "More about IKEA assembly →",
  },
  {
    code: "M",
    title: "Full Apartment Setup",
    audience: "Ideal for expats, couples, and 1–2 bedroom apartments",
    desc: "We set up your entire home so you can move in without disruption.",
    includes: ["Multi-room planning", "Delivery management", "Full assembly and setup", "Organization", "Packaging removal", "Final walkthrough"],
    price: "Starting from €1,290",
    img: PREMIUM_IMG,
    tag: "Most booked",
    link: null,
  },
  {
    code: "L",
    title: "White Glove Relocation Concierge",
    audience: "Designed for families, executives, and premium relocations",
    desc: "Complete done-for-you setup with priority coordination and ongoing support.",
    includes: ["Pre-arrival planning", "Full apartment setup", "Priority scheduling", "Custom coordination", "Premium support", "Post-move assistance"],
    price: "Custom quote",
    img: CONCIERGE_IMG,
    tag: "Premium",
    link: null,
  },
];

const whyUs = [
  { icon: Languages, t: "English-first communication", d: "Spanish also available." },
  { icon: MapPin, t: "Munich-specific knowledge", d: "Local quirks, local solutions." },
  { icon: Plane, t: "Designed for expat needs", d: "Built around international life." },
  { icon: Zap, t: "Fast turnaround", d: "Apartment ready in 48 hours when needed." },
  { icon: Sparkles, t: "White-glove experience", d: "From first email to last screw." },
  { icon: HeartHandshake, t: "Support beyond the move", d: "We stay around as your needs change." },
];

const faqs = [
  { q: "Can you help after I've already moved in?", a: "Yes — many clients use our service after moving to improve, expand, or adjust their setup." },
  { q: "Do you buy furniture for me?", a: "We coordinate based on your approved product list and preferences." },
  { q: "Can you help before I arrive?", a: "Yes — planning and coordination can begin remotely if access to the apartment can be provided." },
  { q: "Do you only work with IKEA?", a: "No — IKEA may be part of the solution, but we focus on practical setup. We also work with Home24, XXXLutz, and others." },
  { q: "Do you install kitchens, electrical systems, or plumbing?", a: "No — specialized regulated work is excluded." },
];

export default function Landing() {
  const { openDialog } = useBookCall();

  useSeo({
    title: "Furniture Assembly Munich for Expats — IKEA, Apartment Setup & Move-In Concierge | MUConcierge",
    description:
      "MUConcierge: end-to-end furniture assembly service for expats in Munich. Let us assemble your furniture professionally and fast, so that you can move into your new apartment stress-free.",
    path: "/",
    lang: "en",
    alternates: { en: "/", de: "/de" },
    jsonLd: [
      faqPageSchema(faqs),
      ...packages.map((p) =>
        serviceSchema({
          name: p.title,
          description: p.desc,
          url: typeof window !== "undefined" ? window.location.origin + (p.link || "/services") : undefined,
        })
      ),
    ],
  });

  // Smooth-scroll to anchor when arriving with /#hash or when hash changes
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SiteHeader />

      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Modern Munich apartment" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-28 md:py-40 lg:py-48">
          <div className="max-w-4xl fade-up">
            <p className="tag-line text-[#FAF9F6]/80 mb-6">MUConcierge · Munich Uncomplicated</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#FAF9F6] tracking-tighter leading-[1.05]">
              Furniture assembly
              <span className="block italic font-light text-[#E8C9B6]">in Munich.</span>
            </h1>
            <h2 className="mt-6 font-serif text-2xl sm:text-3xl text-[#FAF9F6]/95 italic font-light tracking-tight leading-tight max-w-3xl">
              MUConcierge: White-glove end-to-end furniture assembly service for expats in Munich.
            </h2>
            <p className="mt-8 text-lg text-[#FAF9F6]/85 max-w-2xl leading-relaxed font-light">
              We help expats and international professionals set up, adapt, and manage their Munich home — from first arrival to everyday living. Furniture planning, delivery coordination, assembly, setup, and ongoing support — handled for you.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => openDialog({ source: "hero", service: "Discovery call" })}
                className="rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] text-base px-8 py-6"
                data-testid="hero-discovery-btn"
              >
                Book a Free Discovery Call
                <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button
                size="lg"
                onClick={() => openDialog({ source: "hero-48h", service: "48-hour rush setup" })}
                variant="outline"
                className="rounded-sm bg-transparent border-[#FAF9F6]/40 text-[#FAF9F6] hover:bg-[#FAF9F6]/10 hover:text-[#FAF9F6] text-base px-8 py-6"
                data-testid="hero-48h-btn"
              >
                Get Your Apartment Ready in 48 Hours
              </Button>
            </div>
            <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-[#FAF9F6]/80">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Fully insured</div>
              <div className="flex items-center gap-2"><Globe2 className="w-4 h-4" strokeWidth={1.5} /> English-speaking team</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5} /> Munich-based</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BANNER */}
      <section className="border-y border-[#E5E2DC] bg-[#F5F3EF]" data-testid="trust-banner">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-7 text-center">
          <p className="tag-line text-[#1C3326]">
            Designed for expats, professionals, families, and corporate relocations in Munich.
          </p>
        </div>
      </section>

      {/* USE CASES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20 md:py-24" data-testid="use-cases">
        <p className="tag-line text-[#C26D5C] mb-3 text-center">Use cases</p>
        <p className="font-serif text-3xl sm:text-4xl text-[#1A201C] tracking-tight text-center mb-12 max-w-3xl mx-auto leading-tight">
          When MUConcierge makes sense.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {useCases.map((u, i) => (
            <div
              key={i}
              className="bg-white border border-[#E5E2DC] p-5 flex items-start gap-3 transition-all duration-300 hover:border-[#1C3326]"
              data-testid={`usecase-${i}`}
            >
              <div className="w-1.5 h-1.5 bg-[#C26D5C] mt-2 shrink-0" />
              <p className="text-[#1A201C] text-sm leading-relaxed">{u}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-[#F5F3EF] border-y border-[#E5E2DC]" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-5">
            <p className="tag-line text-[#C26D5C] mb-4">The problem</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05]">
              Moving to Munich is exciting.
              <span className="block italic font-light text-[#C26D5C]">Furniture assembly is not.</span>
            </h2>
            <p className="mt-6 text-[#5C635A] leading-relaxed font-light">
              Expats in Munich often struggle with:
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-3">
              {problems.map((p, i) => (
                <li key={i} className="flex items-start gap-3 bg-white border border-[#E5E2DC] p-5">
                  <span className="mt-2 w-1.5 h-1.5 bg-[#C26D5C] shrink-0" />
                  <span className="text-[#1A201C] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 border-l-2 border-[#C26D5C] pl-6 py-2">
              <p className="font-serif text-xl text-[#1A201C] italic mb-2">We take care of the setup — and everything that comes after.</p>
              <p className="text-[#5C635A] text-sm">Arrive — or continue living — in a functional, comfortable, fully set up home.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section id="solution" className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32" data-testid="solution-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
          <div className="md:col-span-7">
            <p className="tag-line text-[#C26D5C] mb-4">White Glove Home Setup & Concierge</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A201C] leading-[1.05]">
              Full setup and furniture installation for your Munich home —
              <span className="italic font-light text-[#C26D5C]"> without the chaos.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E5E2DC] p-8 sm:p-10" data-testid="solution-movein">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#1C3326] flex items-center justify-center">
                <Rocket className="w-5 h-5 text-[#FAF9F6]" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl text-[#1A201C]">Move-In & Initial Setup</h3>
            </div>
            <ul className="space-y-3 text-[#5C635A]">
              {moveInItems.map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="mt-2 w-1 h-1 bg-[#C26D5C] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-[#E5E2DC] p-8 sm:p-10" data-testid="solution-beyond">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#C26D5C] flex items-center justify-center">
                <HeartHandshake className="w-5 h-5 text-[#FAF9F6]" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl text-[#1A201C]">Beyond the Move — Ongoing Support</h3>
            </div>
            <ul className="space-y-3 text-[#5C635A]">
              {beyondItems.map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="mt-2 w-1 h-1 bg-[#C26D5C] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-[#1C3326] text-[#FAF9F6]" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
            <div className="md:col-span-7">
              <p className="tag-line text-[#E8C9B6] mb-4">How it works</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                Furniture installation service in five steps. Zero stress.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[#FAF9F6]/15">
            {steps.map((s) => (
              <div key={s.n} className="bg-[#1C3326] p-7 md:p-8" data-testid={`step-${s.n}`}>
                <div className="font-serif text-4xl text-[#E8C9B6] mb-5">{s.n}</div>
                <h3 className="font-serif text-xl mb-3">{s.title}</h3>
                <p className="text-[#FAF9F6]/70 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28" data-testid="audience-section">
        <p className="tag-line text-[#C26D5C] mb-3 text-center">Who this is for</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight text-center mb-14 leading-tight">
          Perfect furniture assembly service for the Munich newcomer — and everyone after.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {audiences.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="bg-white border border-[#E5E2DC] p-6 flex items-start gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              data-testid={`audience-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="w-10 h-10 bg-[#F5F3EF] flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#1C3326]" strokeWidth={1.5} />
              </div>
              <p className="text-[#1A201C] text-sm leading-relaxed mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MORE THAN JUST MOVING */}
      <section id="services" className="bg-[#F5F3EF] border-y border-[#E5E2DC]" data-testid="more-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start">
          <div className="md:col-span-5">
            <p className="tag-line text-[#C26D5C] mb-4">More than just moving</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05]">
              We support expats with furniture assembly at every stage of living in Munich.
            </h2>
            <p className="mt-6 text-[#5C635A] leading-relaxed font-light">
              Common situations we help with — from your first night to years later.
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-2.5">
              {moreSituations.map((s, i) => (
                <li key={i} className="bg-white border border-[#E5E2DC] p-5 flex items-start gap-3">
                  <span className="font-serif text-xs text-[#C26D5C] mt-0.5 tracking-wider">0{i + 1}</span>
                  <span className="text-[#1A201C] text-sm leading-relaxed flex-1">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32" data-testid="packages-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
          <div className="md:col-span-6">
            <p className="tag-line text-[#C26D5C] mb-4">Choose your Munich home setup package</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A201C] leading-[1.05]">
              Three ways to land soft in Munich with turn-key furniture installation.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((s) => (
            <div
              key={s.code}
              className="bg-white border border-[#E5E2DC] flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              data-testid={`service-card-${s.code.toLowerCase()}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#F5F3EF] relative">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 w-10 h-10 bg-[#1C3326] text-[#FAF9F6] flex items-center justify-center font-serif text-xl">
                  {s.code}
                </div>
              </div>
              <div className="p-8 flex flex-col flex-1">
                <p className="tag-line text-[#C26D5C] mb-3">{s.tag}</p>
                <h3 className="font-serif text-2xl text-[#1A201C] mb-2">{s.title}</h3>
                <p className="text-[#5C635A] text-xs italic mb-4">{s.audience}</p>
                <p className="text-[#5C635A] text-sm leading-relaxed mb-5">{s.desc}</p>

                <ul className="space-y-2 mb-6 text-sm">
                  {s.includes.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <span className="mt-2 w-1 h-1 bg-[#C26D5C] shrink-0" />
                      <span className="text-[#5C635A]">{it}</span>
                    </li>
                  ))}
                </ul>

                {s.link && (
                  <Link
                    to={s.link}
                    className="inline-flex items-center gap-1 text-sm text-[#1C3326] underline underline-offset-4 decoration-[#C26D5C]/40 hover:decoration-[#C26D5C] mb-4"
                    data-testid={`learn-more-${s.code.toLowerCase()}`}
                  >
                    {s.learnMore}
                  </Link>
                )}

                <div className="mt-auto pt-5 border-t border-[#E5E2DC] flex items-center justify-between gap-3">
                  <p className="font-serif text-lg text-[#1A201C]">{s.price}</p>
                  <Button
                    size="sm"
                    onClick={() => openDialog({ source: `package-${s.code}`, service: `${s.code} — ${s.title}` })}
                    className="rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6]"
                    data-testid={`schedule-call-${s.code.toLowerCase()}`}
                  >
                    Schedule a call
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add-on */}
        <div className="mt-8 bg-[#1C3326] text-[#FAF9F6] p-8 sm:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center" data-testid="addon-section">
          <div className="md:col-span-2">
            <p className="tag-line text-[#E8C9B6]">Add-on</p>
          </div>
          <div className="md:col-span-7">
            <h3 className="font-serif text-2xl sm:text-3xl mb-2">Post-Move Support</h3>
            <p className="text-[#FAF9F6]/75 text-sm leading-relaxed">
              Flexible support after your move: additional assembly · returns &amp; replacements · adjustments and upgrades.
            </p>
          </div>
          <div className="md:col-span-3 flex md:justify-end">
            <Button
              onClick={() => openDialog({ source: "addon", service: "Post-Move Support add-on" })}
              variant="outline"
              className="rounded-sm bg-transparent border-[#E8C9B6] text-[#E8C9B6] hover:bg-[#E8C9B6] hover:text-[#1C3326]"
              data-testid="addon-btn"
            >
              Ask about add-on
            </Button>
          </div>
        </div>
      </section>

      {/* CORPORATE */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center" data-testid="corporate-section">
        <div className="md:col-span-6">
          <div className="aspect-[4/3] overflow-hidden">
            <img src={ABOUT_IMG} alt="Corporate relocation" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-6">
          <p className="tag-line text-[#C26D5C] mb-4">Corporate relocation</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05] mb-6">
            Furniture assembly partner for HR teams and international hires.
          </h2>
          <p className="text-[#5C635A] leading-relaxed font-light mb-6">
            We support not only move-ins, but the full employee housing lifecycle:
          </p>
          <ul className="space-y-2 text-[#1A201C] text-sm">
            {[
              "Employee apartment setup",
              "Time-sensitive onboarding",
              "Standardized housing setups",
              "Ongoing support for furniture and adjustments",
            ].map((x) => (
              <li key={x} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 bg-[#C26D5C]" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <Button
            onClick={() => openDialog({ source: "corporate", service: "Corporate relocation" })}
            className="mt-8 rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6]"
            data-testid="corporate-btn"
          >
            Talk to us about corporate
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#F5F3EF] border-y border-[#E5E2DC]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-6">
              <p className="tag-line text-[#C26D5C] mb-4">Why us</p>
              <p className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight leading-[1.05]">
                We are not just furniture assemblers.
                <span className="block italic font-light text-[#C26D5C]">We are your local partner in Munich.</span>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E2DC]">
            {whyUs.map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-[#FAF9F6] p-7" data-testid={`why-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <Icon className="w-5 h-5 text-[#1C3326] mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl text-[#1A201C] mb-2">{t}</p>
                <p className="text-[#5C635A] text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-24 text-center" data-testid="testimonial-section">
        <p className="tag-line text-[#C26D5C] mb-6">Pilot client</p>
        <p className="font-serif text-3xl sm:text-4xl text-[#1A201C] leading-snug italic font-light max-w-3xl mx-auto">
          "Arriving in Munich to a fully set up apartment made the transition effortless."
        </p>
        <p className="mt-6 text-sm text-[#5C635A]">— Pilot Client</p>
      </section>

      {/* MUNICH STRIP */}
      <section className="relative h-[40vh] min-h-[320px]">
        <img src={MUNICH_IMG} alt="Munich" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1C3326]/60" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 h-full flex items-center">
          <div className="max-w-2xl">
            <p className="tag-line text-[#E8C9B6] mb-4">Local. Reliable. English.</p>
            <p className="font-serif text-4xl sm:text-5xl text-[#FAF9F6] leading-[1.05] tracking-tight">
              We know Schwabing, Maxvorstadt, and that elevator that never works in Au-Haidhausen.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 py-24 md:py-32" data-testid="faq-section">
        <p className="tag-line text-[#C26D5C] mb-4 text-center">Frequently asked</p>
        <p className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight text-center mb-14">
          Questions, answered.
        </p>
        <Accordion type="single" collapsible className="space-y-2">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border border-[#E5E2DC] bg-white px-6"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="font-serif text-lg text-[#1A201C] hover:no-underline text-left">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#5C635A] leading-relaxed font-light">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#1C3326]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-24 text-center">
          <p className="font-serif text-4xl sm:text-6xl text-[#FAF9F6] tracking-tight leading-[1.05]">
            Skip the first-week chaos —
            <span className="block italic text-[#E8C9B6]">and everything after.</span>
          </p>
          <p className="mt-6 text-[#FAF9F6]/80 max-w-xl mx-auto font-light">
            Move into Munich with confidence. Live here with support.
          </p>
          <Button
            size="lg"
            onClick={() => openDialog({ source: "final-cta", service: "Discovery call" })}
            className="mt-10 rounded-sm bg-[#FAF9F6] text-[#1C3326] hover:bg-[#E8C9B6] px-10 py-6 text-base"
            data-testid="footer-book-btn"
          >
            Book Your Free Discovery Call
            <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
